import React from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { CheckCircle, AlertCircle, AlertTriangle } from 'lucide-react';

interface ConfidenceBadgeProps {
  score: number;
  field?: string;
  showPercentage?: boolean;
}

export function ConfidenceBadge({
  score,
  field,
  showPercentage = false,
}: ConfidenceBadgeProps) {
  const percentage = Math.round(score * 100);
  
  const getConfig = () => {
    if (percentage >= 95) {
      return {
        label: 'High',
        variant: 'default' as const,
        icon: CheckCircle,
        color: 'text-green-600',
      };
    } else if (percentage >= 85) {
      return {
        label: 'Medium',
        variant: 'secondary' as const,
        icon: AlertTriangle,
        color: 'text-yellow-600',
      };
    } else {
      return {
        label: 'Low',
        variant: 'destructive' as const,
        icon: AlertCircle,
        color: 'text-red-600',
      };
    }
  };

  const config = getConfig();
  const Icon = config.icon;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant={config.variant} className="gap-1 cursor-help">
            <Icon className="h-3 w-3" />
            {showPercentage ? `${percentage}%` : config.label}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <div className="text-sm">
            {field && <div className="font-medium">{field}</div>}
            <div className="text-muted-foreground">
              Confidence: {percentage}%
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
